import React from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../translations/content';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiCalendar, FiCode, FiCheck, FiZap, FiTarget, FiArrowRight } = FiIcons;

const USPSection = () => {
  const { lang } = useLanguage();
  const t = translations[lang].hero.usp;

  return (
    <section className="py-14 bg-gray-900 text-white overflow-hidden relative">
      <div className="absolute top-0 left-0 w-full h-full opacity-10 pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-500 rounded-full blur-[120px]" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-purple-500 rounded-full blur-[120px]" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center mb-12">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="inline-flex items-center space-x-2 px-4 py-1.5 rounded-full bg-blue-500/10 text-blue-400 text-xs font-black uppercase tracking-[0.3em] mb-3 border border-blue-500/20"
          >
            <SafeIcon icon={FiZap} />
            <span>{t.badge}</span>
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-2xl md:text-4xl font-black mb-4 tracking-tight"
          >
            {t.title}
          </motion.h2>
          <motion.div
            initial={{ scaleX: 0 }}
            whileInView={{ scaleX: 1 }}
            viewport={{ once: true }}
            className="h-1 w-20 bg-gradient-to-r from-blue-600 to-purple-600 mx-auto rounded-full"
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8">
          {/* Column 1: Consulting Sessions */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="group relative"
          >
            <div className="bg-gradient-to-br from-gray-800/50 to-gray-900 p-6 md:p-10 rounded-[3rem] border border-gray-700/50 shadow-2xl h-full flex flex-col transition-all duration-500 hover:border-blue-500/50 hover:bg-gray-800/80">
              <div className="flex justify-between items-start mb-7">
                <div className="w-13 h-13 bg-blue-600/20 rounded-2xl flex items-center justify-center group-hover:scale-110 group-hover:bg-blue-600 transition-all duration-500 shadow-lg p-3">
                  <SafeIcon icon={FiCalendar} className="text-blue-400 group-hover:text-white text-2xl" />
                </div>
                <div className="px-4 py-1.5 bg-green-500/10 text-green-400 text-[10px] font-black uppercase tracking-widest rounded-full border border-green-500/20">
                  {t.column1.badge}
                </div>
              </div>

              <h3 className="text-2xl md:text-3xl font-black mb-1.5 text-white group-hover:text-blue-400 transition-colors">
                {t.column1.title}
              </h3>
              <p className="text-blue-400 font-bold text-xs uppercase tracking-widest mb-5 opacity-80">
                {t.column1.subtitle}
              </p>

              <p className="text-gray-400 text-base leading-relaxed mb-7 flex-grow font-medium">
                {t.column1.desc}
              </p>

              <div className="space-y-3 pt-6 border-t border-gray-800/50">
                <div className="flex items-center space-x-3 text-sm text-gray-300">
                  <div className="w-5 h-5 rounded-lg bg-blue-500/10 flex items-center justify-center flex-shrink-0">
                    <SafeIcon icon={FiCheck} className="text-blue-400 text-xs" />
                  </div>
                  <span className="font-medium">Direct implementation of your strategy</span>
                </div>
                <div className="flex items-center space-x-3 text-sm text-gray-300">
                  <div className="w-5 h-5 rounded-lg bg-blue-500/10 flex items-center justify-center flex-shrink-0">
                    <SafeIcon icon={FiTarget} className="text-blue-400 text-xs" />
                  </div>
                  <span className="font-medium">Personalized roadmap review & feedback</span>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Column 2: Co-Creation Build */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="group relative"
          >
            <div className="bg-gradient-to-br from-gray-800/50 to-gray-900 p-6 md:p-10 rounded-[3rem] border border-gray-700/50 shadow-2xl h-full flex flex-col transition-all duration-500 hover:border-purple-500/50 hover:bg-gray-800/80">
              <div className="flex justify-between items-start mb-7">
                <div className="w-13 h-13 bg-purple-600/20 rounded-2xl flex items-center justify-center group-hover:scale-110 group-hover:bg-purple-600 transition-all duration-500 shadow-lg p-3">
                  <SafeIcon icon={FiCode} className="text-purple-400 group-hover:text-white text-2xl" />
                </div>
                <div className="px-4 py-1.5 bg-purple-500/10 text-purple-400 text-[10px] font-black uppercase tracking-widest rounded-full border border-purple-500/20">
                  {t.column2.badge}
                </div>
              </div>

              <h3 className="text-2xl md:text-3xl font-black mb-1.5 text-white group-hover:text-purple-400 transition-colors">
                {t.column2.title}
              </h3>
              <p className="text-purple-400 font-bold text-xs uppercase tracking-widest mb-5 opacity-80">
                {t.column2.subtitle}
              </p>

              <p className="text-gray-400 text-base leading-relaxed mb-7 flex-grow font-medium">
                {t.column2.desc}
              </p>

              <div className="space-y-3 pt-6 border-t border-gray-800/50">
                <div className="flex items-center space-x-3 text-sm text-gray-300">
                  <div className="w-5 h-5 rounded-lg bg-purple-500/10 flex items-center justify-center flex-shrink-0">
                    <SafeIcon icon={FiCheck} className="text-purple-400 text-xs" />
                  </div>
                  <span className="font-medium">Live online build sessions together</span>
                </div>
                <div className="flex items-center space-x-3 text-sm text-gray-300">
                  <div className="w-5 h-5 rounded-lg bg-purple-500/10 flex items-center justify-center flex-shrink-0">
                    <SafeIcon icon={FiZap} className="text-purple-400 text-xs" />
                  </div>
                  <span className="font-medium">Receive a production-ready professional MVP</span>
                </div>
              </div>

              <div className="mt-8 p-5 bg-black/40 rounded-[2rem] font-mono text-[11px] text-blue-300 border border-white/5 relative overflow-hidden group/terminal">
                <div className="flex items-center space-x-2 mb-3">
                  <div className="w-2.5 h-2.5 rounded-full bg-red-500/50" />
                  <div className="w-2.5 h-2.5 rounded-full bg-yellow-500/50" />
                  <div className="w-2.5 h-2.5 rounded-full bg-green-500/50" />
                  <span className="text-gray-600 text-[9px] ml-2">co-creation-session.terminal</span>
                </div>
                <p className="text-gray-500 mb-1">// Starting MVP Build Process...</p>
                <p><span className="text-purple-400">stup-on</span> build <span className="text-green-400">--with-founder</span></p>
                <div className="flex mt-3 space-x-1">
                  <div className="h-1.5 bg-gray-800 w-full rounded-full overflow-hidden">
                    <motion.div
                      animate={{ x: ['100%', '-100%'] }}
                      transition={{ duration: 1.5, repeat: Infinity, ease: "linear" }}
                      className="h-full w-1/3 bg-gradient-to-r from-blue-500 to-purple-500"
                    />
                  </div>
                </div>
                <div className="absolute top-0 right-0 p-4 opacity-10 group-hover/terminal:opacity-30 transition-opacity">
                  <SafeIcon icon={FiArrowRight} className="text-4xl -rotate-45" />
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default USPSection;